import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, money } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChartLineDown, MagnifyingGlass } from "@phosphor-icons/react";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const TYPE_LABEL = {
  cash_payment: "Cash payment",
  call_limit_purchase: "Call-limit purchase",
  tts_charge: "TTS",
  ai_model_charge: "AI Model",
  stt_charge: "STT",
  adjustment: "Adjustment",
  renewal_charge: "Renewal charge",
  white_label_charge: "White labeling",
  white_label_renewal: "White labeling renewal",
  refund: "Refund",
};

function startOfDay(isoDate) {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function endOfDay(isoDate) {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Read-only billing/transaction history for the caller's own account.
export function TransactionHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  // Applied filters (so typing doesn't filter until Apply / on change for dates we apply live)
  const [applied, setApplied] = useState({ from: "", to: "", q: "", type: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/transactions");
        setRows(data || []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const applyFilters = () => {
    setApplied({ from: fromDate, to: toDate, q: query.trim(), type: typeFilter });
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setQuery("");
    setTypeFilter("");
    setApplied({ from: "", to: "", q: "", type: "" });
  };

  const filtered = useMemo(() => {
    const from = startOfDay(applied.from);
    const to = endOfDay(applied.to);
    const q = (applied.q || "").toLowerCase();
    return (rows || []).filter((t) => {
      const created = t.created_at ? new Date(t.created_at) : null;
      if (from && (!created || created < from)) return false;
      if (to && (!created || created > to)) return false;
      if (applied.type && t.txn_type !== applied.type) return false;
      if (q) {
        const hay = [
          TYPE_LABEL[t.txn_type] || t.txn_type,
          t.description,
          t.reference,
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, applied]);

  const totalSpentMinor = useMemo(
    () => filtered.reduce((sum, t) => (t.amount_minor < 0 ? sum + Math.abs(t.amount_minor) : sum), 0),
    [filtered],
  );

  const totalCreditsMinor = useMemo(
    () => filtered.reduce((sum, t) => (t.amount_minor > 0 ? sum + t.amount_minor : sum), 0),
    [filtered],
  );

  const typeOptions = useMemo(() => {
    const set = new Set((rows || []).map((t) => t.txn_type).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const filtersActive = !!(applied.from || applied.to || applied.q || applied.type);

  return (
    <div className="space-y-4" data-testid="transactions-section">
      {/* 2. Total amount spent */}
      <div
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        data-testid="billing-spend-summary"
      >
        <div className="rounded-xl border border-border bg-card card-shadow p-4 sm:col-span-1">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-destructive/10 text-destructive">
              <ChartLineDown className="h-5 w-5" weight="duotone" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Total amount spent
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-destructive" data-testid="billing-total-spent">
                {loading ? "…" : money(totalSpentMinor)}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {filtersActive ? "Debits in the selected range" : "All-time debits on your cash balance"}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card card-shadow p-4 sm:col-span-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Credits received
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-[hsl(var(--success))]" data-testid="billing-total-credits">
            {loading ? "…" : money(totalCreditsMinor)}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {filtersActive ? "In the selected range" : "Payments & credits"}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card card-shadow p-4 sm:col-span-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Transactions
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums" data-testid="billing-txn-count">
            {loading ? "…" : filtered.length}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {filtersActive ? `of ${rows.length} total` : "In history"}
          </p>
        </div>
      </div>

      {/* 3. Search / date filters */}
      <div
        className="rounded-xl border border-border bg-card card-shadow p-4"
        data-testid="transactions-filters"
      >
        <div className="mb-3 flex items-center gap-2">
          <MagnifyingGlass className="h-4 w-4 text-primary" weight="duotone" />
          <h3 className="text-sm font-semibold">Search &amp; filter</h3>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="txn-from">From date</Label>
            <Input
              id="txn-from"
              data-testid="txn-filter-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="txn-to">To date</Label>
            <Input
              id="txn-to"
              data-testid="txn-filter-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="txn-type">Type</Label>
            <select
              id="txn-type"
              data-testid="txn-filter-type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All types</option>
              {typeOptions.map((t) => (
                <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5 lg:col-span-1">
            <Label htmlFor="txn-q">Search</Label>
            <Input
              id="txn-q"
              data-testid="txn-filter-q"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Description or reference"
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={applyFilters}
              className="rounded-lg flex-1"
              data-testid="txn-filter-apply"
            >
              Apply
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={clearFilters}
              className="rounded-lg"
              data-testid="txn-filter-clear"
              disabled={!filtersActive && !fromDate && !toDate && !query && !typeFilter}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* 4. Transaction History table */}
      <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="transactions-table">
        <div className="px-5 py-3.5 border-b border-border flex flex-wrap items-end justify-between gap-2">
          <div>
            <h3 className="font-semibold text-base">Transaction History</h3>
            <p className="text-sm text-muted-foreground">Every movement on your cash balance.</p>
          </div>
          {!loading && (
            <p className="text-xs text-muted-foreground tabular-nums">
              Showing {filtered.length}
              {filtersActive ? ` of ${rows.length}` : ""}
            </p>
          )}
        </div>
        {loading ? (
          <div className="p-5 text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="transactions-empty">
            {rows.length === 0 ? "No transactions yet." : "No transactions match your filters."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance after</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => {
                  const credit = t.amount_minor >= 0;
                  return (
                    <TableRow key={t.id} data-testid={`transaction-row-${t.id}`}>
                      <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{fmtDate(t.created_at)}</TableCell>
                      <TableCell className="text-sm">{TYPE_LABEL[t.txn_type] || t.txn_type}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[260px] truncate" title={t.description || ""}>{t.description || "—"}</TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground max-w-[140px] truncate" title={t.reference || ""}>
                        {t.reference || "—"}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums font-semibold ${credit ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                        {credit ? "+" : "−"}{money(Math.abs(t.amount_minor))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{money(t.balance_after_minor)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
